import Box from "@mui/material/Box";
import { tokens } from "../tokens";
import { Icon } from "./Icon";
import { Typography } from "./Typography";
import type { RenewalSummaryCardProps } from "../types";

/**
 * Shared renewal-summary card head (H2 screens 2, 7 and 8 — TG-5,
 * ADR-20260914-6bk7): the "Renewal summary" eyebrow, the business name as
 * a `display` H1, an optional auto-fit grid of icon+fact rows (the
 * calendar/shield rows H2 repeats identically across all three screens),
 * then whatever screen-specific content the caller supplies as `children`.
 *
 * Built here, not duplicated per screen, because H2 states explicitly that
 * screen 8 reuses "the same card as screen 7's head" — a second,
 * independently-styled copy in each of three feature files would drift
 * from H2's own single pattern the first time one of them changed. Every
 * value here is token-only (ADR-20260914-6bk7): no literal colour, spacing
 * or radius outside `tokens`.
 *
 * `metaRows` is optional and empty by default — screen 2's contributor
 * state (2.3) and screen 7's colleague-submitted/colleague-saved states
 * show no calendar row at all (H2's own markup), so this never renders an
 * empty grid when no rows are supplied.
 */
export function RenewalSummaryCard({ businessName, metaRows, children, testId }: Readonly<RenewalSummaryCardProps>) {
  return (
    <Box
      data-testid={testId}
      sx={{
        backgroundColor: tokens.colour.neutral.white,
        borderRadius: `${tokens.interactionState.radius.surface}px`,
        boxShadow: tokens.interactionState.elevation.card,
        // ADR-20261102-h2fd: H2 screen 2's summary card.
        padding: "clamp(20px, 4vw, 32px)",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: `${tokens.spacing.xxs}px` }}>
        <Typography variant="eyebrow" as="p">
          Renewal summary
        </Typography>
        <Typography variant="display" as="h1">
          {businessName}
        </Typography>
      </Box>
      {metaRows !== undefined && metaRows.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
            gap: `${tokens.spacing.md}px`,
          }}
        >
          {metaRows.map((row) => (
            // Keyed by icon name: within one card's meta grid, H2's own
            // rows (calendar, shield) never repeat.
            <Box key={row.icon} sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
              {/* H2: Icon tone="muted". */}
              <Box component="span" sx={{ display: "block", mt: "2px" }}>
                <Icon name={row.icon} size={22} tone="muted" />
              </Box>
              <Typography variant="body">{row.content}</Typography>
            </Box>
          ))}
        </Box>
      ) : null}
      {children}
    </Box>
  );
}

export type { RenewalSummaryCardMetaRow } from "../types";
